import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Col, Divider, Form, message, Row } from 'antd';
import {
  CustomButton,
  CustomDynamicSelect,
  CustomInput,
  CustomTitle,
} from '../../components';
import { useSpin } from '../../components/SpinContext';
import { prefferedLanguages } from '../../common/static_data';
import { GetSettingsProfile, SubmitSettingsProfile } from '../../apis/settings';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { useToken } from '../../common/helper';
import CustomCheckboxGroup from '../../components/CustomCheckboxGroup';
import TeamsTag from '../../components/TeamsTag';
import SuperAdminBanner from './SuperAdmin/SuperAdminBanner';
import TransferOwnershipModal from './SuperAdmin/TransferOwnershipModal';
import theme from '@/assets/theme';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { mapLanguageToCode } from '../../common/utils/languageMapper';
import SettingRegionalPreferences from './UserProfile/SettingRegionalPreferences';
import SettingAppearance from './UserProfile/SettingAppearance';
import { setDarkModeEnabled } from '@/redux-toolkit/slice/appearanceSlice';
import { isDarkModeEnabled as isDarkModeFeatureEnabled } from '@/utils/featureFlags';

const PhoneInputComponent = PhoneInput?.default ?? PhoneInput;

const notificationBackendToUI = {
  MOBILE: 'SMS',
  WHATSAPP: 'WhatsApp',
};

const SettingsTeamBasedProfile = ({ title }) => {
  const { t } = useTranslation('settings');
  const [form] = Form.useForm();
  const { token, removeToken, decodedUserToken } = useToken();
  const dispatch = useDispatch();
  const isClientTheme = theme.themeType === 2;
  const teamLabel = t(isClientTheme ? 'client' : 'team');
  const teamLabelPlural = t(isClientTheme ? 'clients' : 'teams');

  const { showSpin, hideSpin } = useSpin();

  const [isFormValid, setIsFormValid] = useState(false);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [userTeams, setUserTeams] = useState([]);
  const [openTransfer, setOpenTransfer] = useState(false);
  const handleTransferClick = () => setOpenTransfer(true);
  const handleClose = () => setOpenTransfer(false);
  const handleContinue = () => {
    setOpenTransfer(false);
  };

  const navigate = useNavigate();

  // add country code and flag [mahtab]
  const [selectedCountryCode] = useState();

  const onFinish = (values) => {
    const isSMSorWhatsAppSelected = selectedNotifications.some((opt) =>
      ['SMS', 'WhatsApp'].includes(opt)
    );
    const phoneNumber = values.phone_number?.trim();

    if (isSMSorWhatsAppSelected) {
      if (!phoneNumber || phoneNumber === '') {
        message.error(
          'Phone number is required when SMS or WhatsApp is selected.'
        );
        return;
      }
      const cleaned = phoneNumber.replace(/[^\d]/g, '');
      const isValidPhone =
        /^\+[\d\s()-]+$/.test(phoneNumber) && cleaned.length >= 7;

      if (!isValidPhone) {
        message.error(
          'Please enter a valid phone number if you want to enable opt-in notifications (SMS or WhatsApp).'
        );

        return;
      }
    }

    if (values && Object.keys(values).length > 0) {
      if (selectedCountryCode) {
        values.phone_number = selectedCountryCode?.phone;
      }

      const mappedNotifications = selectedNotifications.map((option) => {
        if (option === 'SMS') return 'MOBILE';
        if (option === 'WhatsApp') return 'WHATSAPP';
        return option;
      });
      const refreshToken = localStorage.getItem('@refreshToken');
      const model = {
        ...values,
        notification_options: mappedNotifications,
        refresh: refreshToken,
      };

      submitProfile(model);
    }
  };

  const unauthorize = useCallback(
    (error) => {
      if (error && error?.status === 401) {
        removeToken();
        navigate('/login');
      }
    },
    [navigate, removeToken]
  );

  useEffect(() => {
    showSpin();
    GetSettingsProfile(token)
      .then((res) => {
        const data = res?.data ? res.data : null;
        if (data) {
          if (data?.data?.is_success) {
            let resp = data?.data?.data ? data?.data?.data : null;

            if (resp) {
              form.setFieldsValue({
                id: resp.id,
                name: resp.name,
                email: resp.email,
                title: resp.title,
                // country_code:resp.country_code,
                phone_number: resp.phone_number,
                time_zone_enabled: resp.time_zone_enabled,
                user_time_zone: resp.user_time_zone,
                currency: resp.currency || 'USD',
                date_format: resp.date_format || 'MM/DD/YYYY',
                dark_mode_enabled: !!resp.dark_mode_enabled,
                preferred_language: !resp?.preferred_language
                  ? 'English'
                  : resp?.preferred_language &&
                      (resp?.preferred_language?.toLowerCase() ===
                        'english(us)' ||
                        resp?.preferred_language?.toLowerCase() === 'english')
                    ? 'English'
                    : resp?.preferred_language,
              });
              setSelectedNotifications(
                (resp?.notification_options || []).map(
                  (opt) => notificationBackendToUI[opt] || opt
                )
              );
              setUserTeams(resp?.teams || []);
            }
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
      });
  }, [form, hideSpin, showSpin, token, unauthorize]);

  //submit call
  const submitProfile = (model) => {
    setIsFormValid(true);
    showSpin();

    SubmitSettingsProfile(token, model)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            message.success(data?.data?.message);
            localStorage.setItem('@token', res?.data?.data?.access_token);
            if (res?.data?.data?.refresh) {
              localStorage.setItem('@refreshToken', res?.data?.data?.refresh);
            }

            // Apply language change immediately if preferred_language was updated
            if (model?.preferred_language) {
              const languageCode = mapLanguageToCode(model.preferred_language);
              if (i18n.language !== languageCode) {
                i18n.changeLanguage(languageCode);
              }
            }

            // Apply dark mode immediately -- don't wait for the next JWT
            // decode, since the token here is stored via a raw
            // localStorage.setItem (above) which doesn't itself trigger a
            // re-render anywhere that would re-read it. Gated the same way
            // as useAppearanceSync: with REACT_APP_ENABLE_DARK_MODE off, a
            // preference saved before the flag was flipped off (e.g. synced
            // from UAT) must not turn the theme dark here either.
            dispatch(
              setDarkModeEnabled(
                isDarkModeFeatureEnabled() &&
                  !!res?.data?.data?.data?.dark_mode_enabled
              )
            );
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        unauthorize(error);
      })
      .finally(() => {
        hideSpin();
        setIsFormValid(false);
      });
  };

  return (
    <>
      <CustomTitle
        text={title}
        fontSize={24}
        fontWeight='700'
        lineHeight='32px'
        color='var(--text-primary)'
        marginBottom={0}
      />

      <Form
        form={form}
        name='ProfileForm'
        layout='vertical'
        onFinish={onFinish}
        autoComplete='off'
        initialValues={{
          name: '',
          id: null,
          email: '',
          phone_number: selectedCountryCode,
          designation: null,
          time_zone_enabled: false,
          user_time_zone: null,
          preferred_language: null,
        }}
      >
        <div style={{ marginBottom: '45px' }}>
          <Row gutter={14}>
            <Col xs={24} sm={24} md={12} lg={8} xl={8} xxl={8}>
              <Form.Item
                className='custom-input form-input-style'
                label={t('name')}
                name='name'
                rules={[
                  {
                    required: true,
                    message: t('pleaseInputYourName', 'Please input your name'),
                  },
                  {
                    min: 3,
                    message: t(
                      'nameMinLength',
                      'Name must be at least 3 characters long'
                    ),
                  },
                  {
                    pattern: /^[A-Za-z\s'-]+$/,
                    message: t(
                      'pleaseEnterValidName',
                      'Please enter a valid name (letters, spaces, hyphens only)'
                    ),
                  },
                ]}
                style={{ marginBottom: '0px' }}
              >
                <CustomInput
                  placeholder={t('enterName', 'Enter name')}
                  height='36px'
                  border='0px'
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={8} xxl={8}>
              <Form.Item
                className='custom-input form-input-style'
                label={t('title')}
                name='title'
                rules={[
                  {
                    required: true,
                    message: t('pleaseInputYourRole', 'Please input your role'),
                  },
                  {
                    validator: (_, value) => {
                      if (value && value.trim() === '') {
                        return Promise.reject(
                          new Error(
                            t(
                              'onlySpacesNotAllowed',
                              'Only spaces are not allowed'
                            )
                          )
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                style={{ marginBottom: '0px' }}
              >
                <CustomInput
                  placeholder={t('enterYourRole', 'Enter your role')}
                  height='36px'
                  border='0px'
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={14} style={{ marginTop: '30px' }}>
            <Col xs={24} sm={24} md={12} lg={8} xl={8} xxl={8}>
              <Form.Item
                className='custom-input form-input-style'
                label={t('email')}
                name='email'
                rules={[
                  {
                    required: true,
                    message: t(
                      'pleaseEnterYourEmail',
                      'Please enter your email'
                    ),
                  },
                  {
                    type: 'email',
                    message: t('enterValidEmail', 'Enter valid email'),
                  },
                ]}
                style={{ marginBottom: '0px' }}
              >
                <CustomInput
                  placeholder={t('enterEmail', 'Enter email')}
                  height='36px'
                  border='1px solid #E8E8E8'
                  bgColor='var(--surface-container)'
                  disabled={true}
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='38px'
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={12} lg={8} xl={8} xxl={8}>
              <Form.Item
                label={t('phoneNumberOptional')}
                name='phone_number'
                style={{ marginBottom: '0px' }}
                initialValue=''
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.resolve(); // Optional
                      }
                      try {
                        if (isValidPhoneNumber(value)) {
                          return Promise.resolve();
                        } else {
                          return Promise.reject(
                            new Error(
                              t('invalidPhoneNumber', 'Invalid phone number')
                            )
                          );
                        }
                      } catch {
                        return Promise.reject(
                          new Error(
                            t('invalidPhoneNumber', 'Invalid phone number')
                          )
                        );
                      }
                    },
                  },
                ]}
              >
                <PhoneInputComponent
                  country={'us'}
                  value={form.getFieldValue('phone_number')}
                  onChange={(value, countryData, e, formattedValue) => {
                    form.setFieldsValue({ phone_number: formattedValue });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={14} style={{ marginTop: '24px' }}>
            <Col xs={24} sm={24} md={12} lg={8} xl={8} xxl={8}>
              <Form.Item
                className='custom-input form-input-style'
                label={t('preferredLanguage')}
                name='preferred_language'
                rules={[
                  {
                    required: false,
                    message: t('pleaseSelectValue', 'Please select value'),
                  },
                ]}
                style={{ marginBottom: '0px' }}
              >
                <CustomDynamicSelect
                  placeholder={t('select', 'Select')}
                  height='36px'
                  border='0px'
                  bgColor='var(--light-gray)'
                  textColor='var(--text-secondary)'
                  borderRadius='8px'
                  placeholderColor='var(--text-secondary)'
                  lineHeight='16px'
                  options={prefferedLanguages}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row
            gutter={14}
            gap={4}
            style={{
              marginTop: '24px',
              marginLeft: '0px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Row gutter={16}>
              <Col span={24}>
                <CustomTitle
                  marginBottom={'10px'}
                  text={t('optInNotifications')}
                  fontSize={14}
                  fontWeight='600'
                />
                <CustomCheckboxGroup
                  options={['SMS', 'WhatsApp']}
                  selectedValues={selectedNotifications}
                  onChange={setSelectedNotifications}
                />
              </Col>
            </Row>

            {userTeams?.length > 0 && (
              <Row gutter={16} style={{ marginTop: '20px' }}>
                <Col span={24}>
                  <CustomTitle
                    text={teamLabel}
                    fontSize={14}
                    fontWeight='600'
                    marginTop='10px'
                  />
                  <TeamsTag teamData={userTeams} />
                </Col>
              </Row>
            )}
            {decodedUserToken()?.is_company_owner && (
              <CustomTitle
                marginTop={'3px'}
                marginBottom={'0px'}
                text={t('superAdminAccess', {
                  teamPlural: teamLabelPlural.toLowerCase(),
                })}
                fontSize={12}
                color='var(--text-secondary)'
                fontWeight='400'
              />
            )}
            {decodedUserToken()?.is_company_owner && (
              <SuperAdminBanner
                onTransfer={handleTransferClick}
                hasTransferRights={decodedUserToken()?.has_transfer_rights}
                subtitle={t('superAdminDescription', {
                  teamPlural: teamLabelPlural?.toLowerCase(),
                })}
              />
            )}
            <TransferOwnershipModal
              open={openTransfer}
              onClose={handleClose}
              onContinue={handleContinue}
            />
            <Divider />

            <SettingRegionalPreferences form={form} />

            <Divider />

            <SettingAppearance form={form} />
          </Row>
        </div>

        <div className='profile-setting-save-btn'>
          <CustomButton
            text={t('save')}
            width={107}
            height={36}
            borderRadius={8}
            bgColor='#000'
            color='#fff'
            border='0px'
            fontSize={14}
            fontWeight={500}
            htmlType='submit'
            isFormValid={isFormValid}
          />
        </div>
      </Form>
    </>
  );
};

export default SettingsTeamBasedProfile;
